import { toast } from "sonner";
import { objectsToCSV, downloadCSV } from "@/lib/utils/export/csv-export";

// ============================================================================
// GENERIC BULK ACTION TYPES
// ============================================================================

/**
 * Minimal mutation contract: bulk handlers only need `mutateAsync`. Entity hooks
 * (`EntityHooks<T>`) return a richer object, but this loose type keeps
 * `getBulkActionsForEntity` generic across entities without forcing every caller
 * to expose `UseMutationResult` internals.
 */
export interface BulkMutationLike<TInput = unknown, TOutput = unknown> {
  mutateAsync: (input: TInput) => Promise<TOutput>;
  isPending?: boolean;
}

export interface BulkActionConfig {
  id: string;
  label: string;
  icon: string;
  variant?: "default" | "destructive";
  handler?: (
    selectedItems: Array<{ id: string } & Record<string, unknown>>,
    mutations: BulkActionMutations,
    entityName: string
  ) => Promise<void>;
}

/** Per-id outcome counts from a single-request batch update, for the toast. */
export interface BulkUpdateResult {
  successful: number;
  failed: number;
  total: number;
  message?: string;
}

export interface BulkActionMutations {
  update?: BulkMutationLike<{ id: string } & Record<string, unknown>, unknown>;
  delete?: BulkMutationLike<string, unknown>;
  /**
   * Optional single-request batch update backed by an entity batch endpoint
   * (e.g. `POST /api/tickets/bulk`). When present, an entity handler should
   * prefer it over the per-item `update` loop (one request, one approval, one
   * refresh); when absent, handlers fall back to the loop. Supplied only for
   * entities that have a batch endpoint — populated in the React hook layer
   * (where the auth token + Apollo client live), never in these plain handlers.
   */
  bulkUpdate?: (
    ids: string[],
    patch: Record<string, unknown>
  ) => Promise<BulkUpdateResult>;
}

// ============================================================================
// GENERIC BULK ACTION HANDLERS
// ============================================================================

/**
 * Generic bulk delete handler
 */
export const bulkDeleteHandler = async (
  selectedItems: Array<{ id: string } & Record<string, unknown>>,
  mutations: BulkActionMutations,
  entityName: string
) => {
  if (!mutations.delete) {
    throw new Error(`Delete mutation not available for ${entityName}`);
  }

  if (window.confirm(`Delete ${selectedItems.length} ${entityName}?`)) {
    for (const item of selectedItems) {
      await mutations.delete.mutateAsync(item.id);
    }
    toast.success(`${selectedItems.length} ${entityName} deleted`);
  }
};

/**
 * Generic bulk export handler
 */
export const bulkExportHandler = async (
  selectedItems: Array<Record<string, unknown>>,
  _mutations: BulkActionMutations,
  entityName: string
) => {
  const csvData = objectsToCSV(selectedItems, {
    includeHeaders: true,
  });
  downloadCSV(csvData, `${entityName}-${new Date().toISOString().split("T")[0]}.csv`);
  toast.success(`Exported ${selectedItems.length} ${entityName}`);
};

/**
 * Bulk email export handler - opens modal for email export
 */
export const bulkExportEmailsHandler = async (
  _selectedItems: Array<Record<string, unknown>>,
  _mutations: BulkActionMutations,
  _entityName: string
) => {
  // This handler will be overridden by entity-specific implementations
  // For now, just show a message
  toast.info("Email export feature is being initialized...");
};

/**
 * Generic bulk status update handler
 */
export const bulkStatusUpdateHandler = async (
  selectedItems: Array<{ id: string } & Record<string, unknown>>,
  mutations: BulkActionMutations,
  entityName: string,
  statusField: string = "status",
  newStatus: string,
  additionalFields?: Record<string, unknown>
) => {
  if (!mutations.update) {
    throw new Error(`Update mutation not available for ${entityName}`);
  }

  for (const item of selectedItems) {
    const updateData: { id: string } & Record<string, unknown> = {
      id: item.id,
      [statusField]: newStatus,
      ...additionalFields,
    };
    await mutations.update.mutateAsync(updateData);
  }
  toast.success(`${selectedItems.length} ${entityName} updated`);
};
